import {
  CSRF_HEADER,
  DIRECTORY_SKIP_REASONS,
  type DirectorySourceTestResult,
  type DirectorySourceView,
  GOOGLE_WORKSPACE_ADAPTER,
  type GoogleAdminPrivilege,
  type ServiceAccountKey,
  serviceAccountKeySchema,
} from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileKey2, FlaskConical, Info, KeyRound, Plus, Power } from "lucide-react";
import { useId, useRef, useState } from "react";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ok } from "../api-errors.ts";
import { formatList } from "../i18n/format.ts";
import { t } from "../i18n/i18n.ts";
import { Link } from "../router.tsx";
import { useSession } from "../session.tsx";
import { useAdminApi } from "./admin-api.tsx";
import { type SignInProvider, useSignInData } from "./SignInAdmin.tsx";

/*
 * Google Workspace directory sources in the backoffice (openspec change google-workspace,
 * task 5.1): setup with a write-once service-account key and a provisioning scope, the source
 * row's account, scope and skipped counts, and its Test, Enable and Replace key actions.
 */

const sourcesKey = ["admin", "directory", "sources"] as const;
const PRIVILEGES: readonly GoogleAdminPrivilege[] = ["users_read", "groups_read", "org_units_read"];

export const isGoogleSource = (s: DirectorySourceView) => s.adapter === GOOGLE_WORKSPACE_ADAPTER;

/** The key's ID as people compare it with the Google Cloud console (its first 8 characters). */
const shortKeyId = (keyId: string) => keyId.slice(0, 8);

/** Reads a chosen key file; the key itself stays in memory only until it is sent. */
async function readKey(file: File): Promise<ServiceAccountKey | null> {
  try {
    const parsed = serviceAccountKeySchema.safeParse(JSON.parse(await file.text()));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** A test outcome, worded for the source it ran on. */
export function testOutcome(
  name: string,
  result: DirectorySourceTestResult,
): { tone: "success" | "warning" | "danger"; text: string } {
  if (result.ok) {
    return { tone: "success", text: t("directory.google.testPassed", { name }) };
  }
  if (result.missing.length > 0) {
    return {
      tone: "warning",
      text: t("directory.google.testMissing", {
        name,
        list: formatList(result.missing.map((p) => t(`directory.google.privilege.${p}`))),
      }),
    };
  }
  const reason = t(`directory.google.testError.${result.error ?? "unreachable"}`);
  return { tone: "danger", text: t("directory.google.testFailed", { name, reason }) };
}

/** The account, scope and what the last run left out, under a Google source's name. */
export function GoogleSourceDetails({ source }: { source: DirectorySourceView }) {
  const scope = source.scope;
  const skipped = DIRECTORY_SKIP_REASONS.flatMap((reason) => {
    const count = source.skipped?.[reason] ?? 0;
    return count > 0 ? [t(`directory.google.skip.${reason}`, { count })] : [];
  });
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-fg-muted">
      {source.credential ? (
        <span className="inline-flex items-center gap-1 break-all">
          <FileKey2 aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
          {t("directory.google.account", {
            email: source.credential.clientEmail,
            keyId: shortKeyId(source.credential.keyId),
          })}
        </span>
      ) : null}
      {scope ? (
        <span>
          {"group" in scope
            ? t("directory.google.scopeLine.group", { value: scope.group })
            : t("directory.google.scopeLine.orgUnit", { value: scope.orgUnit })}
        </span>
      ) : null}
      {skipped.length > 0 ? (
        <span className="tabular-nums">
          {t("directory.google.skipped", { list: formatList(skipped) })}
        </span>
      ) : null}
    </span>
  );
}

/** Test, Enable (while disabled) and Replace key for one Google source. */
export function GoogleSourceActions(props: {
  source: DirectorySourceView;
  onOutcome: (outcome: { tone: "success" | "warning" | "danger"; text: string }) => void;
  /** End in a table's last column (default), start under a stacked row. */
  align?: "start" | "end";
}) {
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const { source } = props;
  const headers = { headers: { [CSRF_HEADER]: csrfToken } };
  const test = useMutation({
    mutationFn: async () =>
      ok(
        await admin.directory.sources[":sourceId"].test.$post(
          { param: { sourceId: source.id } },
          headers,
        ),
      ),
    onSuccess: (result) => props.onOutcome(testOutcome(source.name, result)),
    onError: (error) => props.onOutcome({ tone: "danger", text: error.message }),
  });
  const enable = useMutation({
    mutationFn: async () =>
      ok(
        await admin.directory.sources[":sourceId"].$patch(
          { param: { sourceId: source.id }, json: { status: "active" } },
          headers,
        ),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sourcesKey }),
    onError: (error) => props.onOutcome({ tone: "danger", text: error.message }),
  });
  const replace = useMutation({
    mutationFn: async (credential: ServiceAccountKey) =>
      ok(
        await admin.directory.sources[":sourceId"].credential.$put(
          { param: { sourceId: source.id }, json: { credential } },
          headers,
        ),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sourcesKey });
      props.onOutcome({
        tone: "success",
        text: t("directory.google.keyReplaced", { name: source.name }),
      });
    },
    onError: (error) => props.onOutcome({ tone: "danger", text: error.message }),
  });
  return (
    <span
      className={cn(
        "inline-flex flex-wrap gap-1",
        props.align === "start" ? "justify-start" : "justify-end",
      )}
    >
      <Button
        variant="ghost"
        size="xs"
        disabled={test.isPending}
        aria-label={t("directory.google.testFor", { name: source.name })}
        onClick={() => test.mutate()}
      >
        <FlaskConical aria-hidden="true" />
        {test.isPending ? t("directory.google.testing") : t("directory.google.test")}
      </Button>
      {source.status === "disabled" ? (
        <Button
          variant="ghost"
          size="xs"
          disabled={enable.isPending}
          onClick={() => enable.mutate()}
        >
          <Power aria-hidden="true" />
          {enable.isPending ? t("directory.google.enabling") : t("directory.google.enable")}
        </Button>
      ) : null}
      <Button
        variant="ghost"
        size="xs"
        disabled={replace.isPending}
        onClick={() => file.current?.click()}
      >
        <KeyRound aria-hidden="true" />
        {replace.isPending ? t("directory.google.replacing") : t("directory.google.replaceKey")}
      </Button>
      <input
        ref={file}
        type="file"
        accept="application/json,.json"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={async (e) => {
          const chosen = e.currentTarget.files?.[0];
          e.currentTarget.value = "";
          if (!chosen) {
            return;
          }
          const key = await readKey(chosen);
          if (key) {
            replace.mutate(key);
          } else {
            props.onOutcome({ tone: "danger", text: t("directory.google.keyInvalid") });
          }
        }}
      />
    </span>
  );
}

/** "Add Google Workspace source", or why it is not offered yet. */
export function GoogleSourceSetup(props: { onAdded: (notice: string) => void }) {
  const { base } = useAdminApi();
  const signin = useSignInData();
  const [open, setOpen] = useState(false);
  const google = (signin.data?.identityProviders ?? []).filter(
    (p) => p.preset === "google_workspace",
  );
  if (!signin.data) {
    return null;
  }
  if (google.length === 0) {
    return (
      <p className="flex items-start gap-1.5 text-sm text-fg-secondary">
        <Info
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <span>
          {t("directory.google.needsIdp")}{" "}
          <Link href={`${base}/signin`} className="text-accent-text underline underline-offset-4">
            {t("directory.google.openSignin")}
          </Link>
        </span>
      </p>
    );
  }
  return (
    <div>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Plus aria-hidden="true" />
        {t("directory.google.add")}
      </Button>
      <AppDialog
        open={open}
        onClose={() => setOpen(false)}
        title={t("directory.google.title")}
        description={t("directory.google.description")}
      >
        <GoogleSourceForm
          providers={google}
          onCancel={() => setOpen(false)}
          onDone={(name) => {
            setOpen(false);
            props.onAdded(t("directory.google.added", { name }));
          }}
        />
      </AppDialog>
    </div>
  );
}

function GoogleSourceForm(props: {
  providers: readonly SignInProvider[];
  onDone: (name: string) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const { admin } = useAdminApi();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(t("directory.google.defaultName"));
  const [identityProviderId, setIdentityProviderId] = useState(props.providers[0]?.id ?? "");
  const [key, setKey] = useState<ServiceAccountKey | null>(null);
  const [keyInvalid, setKeyInvalid] = useState(false);
  const [scopeKind, setScopeKind] = useState<"group" | "orgUnit">("group");
  const [scopeValue, setScopeValue] = useState("");
  const create = useMutation({
    mutationFn: async () =>
      ok(
        await admin.directory.sources.$post(
          {
            json: {
              kind: "pull",
              name: name.trim(),
              adapter: GOOGLE_WORKSPACE_ADAPTER,
              identityProviderId,
              credential: key ?? undefined,
              scope:
                scopeKind === "group"
                  ? { group: scopeValue.trim() }
                  : { orgUnit: scopeValue.trim() },
            },
          },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: () => {
      // The key leaves memory with the form.
      setKey(null);
      void queryClient.invalidateQueries({ queryKey: sourcesKey });
      props.onDone(name.trim());
    },
  });
  const ready = name.trim() && identityProviderId && key && scopeValue.trim();
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) {
          create.mutate();
        }
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-name`}>{t("directory.google.name")}</Label>
          <Input
            id={`${id}-name`}
            value={name}
            maxLength={200}
            onChange={(e) => setName(e.currentTarget.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-idp`}>{t("directory.google.identityProvider")}</Label>
          <NativeSelect
            id={`${id}-idp`}
            value={identityProviderId}
            onChange={(e) => setIdentityProviderId(e.currentTarget.value)}
          >
            {props.providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.displayName}
              </option>
            ))}
          </NativeSelect>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-key`}>{t("directory.google.key")}</Label>
        <Input
          id={`${id}-key`}
          type="file"
          accept="application/json,.json"
          aria-describedby={`${id}-key-help`}
          aria-invalid={keyInvalid || undefined}
          onChange={async (e) => {
            const chosen = e.currentTarget.files?.[0];
            const parsed = chosen ? await readKey(chosen) : null;
            setKey(parsed);
            setKeyInvalid(Boolean(chosen) && !parsed);
          }}
          className="text-[13px] file:mr-3 file:font-medium file:text-fg"
        />
        {key ? (
          <p className="inline-flex items-center gap-1 text-xs break-all text-fg-secondary">
            <FileKey2 aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
            {t("directory.google.keyChosen", {
              email: key.client_email,
              keyId: shortKeyId(key.private_key_id),
            })}
          </p>
        ) : null}
        {keyInvalid ? <InlineError>{t("directory.google.keyInvalid")}</InlineError> : null}
        <p id={`${id}-key-help`} className="text-xs text-fg-muted">
          {t("directory.google.keyHelp")}
        </p>
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1.5 text-sm font-medium text-fg">
          {t("directory.google.scope")}
        </legend>
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-fg">
          {(["group", "orgUnit"] as const).map((kind) => (
            <label key={kind} className="inline-flex items-center gap-2">
              <input
                type="radio"
                name={`${id}-scope`}
                value={kind}
                checked={scopeKind === kind}
                onChange={() => setScopeKind(kind)}
                className="size-4 accent-(--action-solid)"
              />
              {kind === "group"
                ? t("directory.google.scopeGroup")
                : t("directory.google.scopeOrgUnit")}
            </label>
          ))}
        </div>
        <Input
          aria-label={
            scopeKind === "group"
              ? t("directory.google.groupEmail")
              : t("directory.google.orgUnitPath")
          }
          value={scopeValue}
          spellCheck={false}
          autoComplete="off"
          inputMode={scopeKind === "group" ? "email" : "text"}
          placeholder={scopeKind === "group" ? "esi-users@example.com" : "/Operations"}
          onChange={(e) => setScopeValue(e.currentTarget.value)}
          aria-describedby={`${id}-scope-help`}
        />
        <p id={`${id}-scope-help`} className="text-xs text-fg-muted">
          {t("directory.google.scopeHelp")}
        </p>
      </fieldset>
      <div className="flex flex-col gap-1.5 rounded-md border border-line bg-surface-sunken px-3 py-2.5">
        <p className="text-sm font-medium text-fg">{t("directory.google.privileges")}</p>
        <ul className="flex flex-col gap-0.5 text-sm text-fg-secondary">
          {PRIVILEGES.map((p) => (
            <li key={p} className="inline-flex items-center gap-1.5">
              <KeyRound
                aria-hidden="true"
                className="size-3 shrink-0 text-fg-muted"
                strokeWidth={1.75}
              />
              {t(`directory.google.privilege.${p}`)}
            </li>
          ))}
        </ul>
        <p className="text-xs text-fg-muted">{t("directory.google.privilegesHelp")}</p>
      </div>
      <p className="text-xs text-fg-muted">{t("directory.google.residency")}</p>
      {create.isError ? <InlineError>{create.error.message}</InlineError> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={props.onCancel}>
          {t("app.cancel")}
        </Button>
        <Button type="submit" disabled={!ready || create.isPending}>
          {create.isPending ? t("directory.google.adding") : t("directory.google.submit")}
        </Button>
      </DialogFooter>
    </form>
  );
}
